import { ForwardedRef, forwardRef, Fragment, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ActionItemButton } from '@components/button/src/action-button';
import { IconDotsVertical } from '@components/icons';
import { MD_VIEWPORT } from '@constants/viewport';
import { Menu, MenuButton, MenuItem, MenuItems, Transition, TransitionChild } from '@headlessui/react';
import { useWindowSize } from '@hooks/use-window-size';
import classNames from 'classnames';

export interface SubmenuAction {
  name: string;
  disabled?: boolean;
}

export interface ButtonAction extends SubmenuAction {
  isDesktop?: boolean;
  active?: boolean;
  action?: (e?: React.MouseEvent<HTMLElement>) => void;
  close?: () => void;
}
export interface Actions {
  name: string;
  disabled?: boolean;
  link?: string;
  action?: (e?: React.MouseEvent<HTMLElement>) => void;
}

interface LinkAction extends SubmenuAction {
  isDesktop?: boolean;
  active?: boolean;
  link: string;
  close?: () => void;
}

export const SubmenuIconLink = forwardRef<HTMLAnchorElement, LinkAction>(
  ({ name, disabled, link, active, close }: LinkAction, ref) => {
    return (
      <Link
        ref={ref}
        key={`action-menu-${name}`}
        id='bottom-sheet'
        className={classNames(
          'flex items-center rounded-lg px-4 xs:px-3 py-3 xs:py-2 text-headline-xs xs:text-subtitle-m w-full text-primary',
          {
            'fill-secondary': !active,
            'fill-secondary-active': active,
            'cursor-pointer-none pointer-events-none fill-secondary-disabled text-transparent-20': disabled,
          }
        )}
        to={link}
        onClick={close}>
        {name}
      </Link>
    );
  }
);

export const SubmenuIconButton = forwardRef(
  ({ name, disabled, action, active, close }: ButtonAction, ref: ForwardedRef<HTMLDivElement>) => {
    const handleClick = (e?: React.MouseEvent<HTMLElement>) => {
      if (e) e.stopPropagation();
      if (close) close();
      if (action) action();
    };
    return (
      <ActionItemButton
        ref={ref}
        key={`option-menu-${name}`}
        active={active ?? false}
        onClick={handleClick}
        text={name}
        disabled={disabled}
      />
    );
  }
);

const MobileSubmenuIcon = ({
  options,
  buttonContent,
  buttonClasses = 'text-gray-700 dark:text-white',
  dotsClasses = 'w-7 h-7',
}: {
  options: Actions[];
  buttonContent?: React.ReactNode;
  buttonClasses?: string;
  dotsClasses?: string;
}) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const root = document.getElementById('root');
  const { t } = useTranslation();
  return (
    <>
      <button
        type='button'
        className={classNames(buttonClasses)}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setShowMobileMenu(!showMobileMenu);
        }}>
        {buttonContent ?? <IconDotsVertical className={classNames(dotsClasses)} />}
      </button>
      {ReactDOM.createPortal(
        <Transition show={showMobileMenu} appear={true}>
          <TransitionChild
            as={Fragment}
            enter='transition ease-out duration-100'
            enterFrom='opacity-0 backdrop-blur-none bg-transparent'
            enterTo='opacity-100 backdrop-blur-xs bg-transparent-30'
            leave='transition delay-100 ease-in duration-100'
            leaveFrom='opacity-100 backdrop-blur-xs bg-transparent-30'
            leaveTo='opacity-0 backdrop-blur-none bg-transparent'>
            <div
              role='presentation'
              id='bottom-sheet'
              className='flex flex-col gap-3 fixed z-50 w-full h-full top-0 left-0 mx-0 backdrop-blur-xs bg-transparent-30 cursor-pointer'
              onClick={(e) => {
                e.stopPropagation();
                setShowMobileMenu(!showMobileMenu);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'esc') setShowMobileMenu(!showMobileMenu);
              }}></div>
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter='transition-all ease-out delay-100 duration-200'
            enterFrom=' opacity-0 sm:-bottom-10'
            enterTo='opactiy-100 sm:bottom-0'
            leave='transition-all ease-in duration-75'
            leaveFrom='opactiy-100 sm:bottom-0'
            leaveTo='opacity-0 sm:-bottom-10'>
            <div className='fixed flex flex-col gap-3 z-50 w-full left-0 bottom-0 right-0 text-primary bg-primary px-6 xs:px-4 py-10 xs:p-8 rounded-tr-lg rounded-tl-lg mx-0 shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)]'>
              <p className='text-left text-body-xs mb-2 text-secondary'>{t('common:optionsLabel')}</p>
              {options.map((option) => {
                return option.link ? (
                  <SubmenuIconLink
                    key={`action-menu-${option.name}`}
                    link={option.link}
                    name={option.name}
                    disabled={option.disabled}
                    close={() => setShowMobileMenu(!showMobileMenu)}
                  />
                ) : (
                  <SubmenuIconButton
                    key={`action-menu-${option.name}`}
                    {...option}
                    close={() => setShowMobileMenu(!showMobileMenu)}
                  />
                );
              })}
            </div>
          </TransitionChild>
        </Transition>,
        root as Element
      )}
    </>
  );
};

export const SubmenuIcon = ({
  options,
  buttonContent,
  className,
  buttonClasses = 'text-primary',
  dotsClasses = 'w-7 h-7',
}: {
  buttonContent?: React.ReactNode;
  options: Actions[];
  className?: string;
  buttonClasses?: string;
  dotsClasses?: string;
}) => {
  const isMobileViewPort = useWindowSize(MD_VIEWPORT as number);
  const { t } = useTranslation();
  if (isMobileViewPort) {
    return (
      <MobileSubmenuIcon
        options={options}
        buttonContent={buttonContent}
        buttonClasses={buttonClasses}
        dotsClasses={dotsClasses}
      />
    );
  }
  return (
    <Menu as='div' className={classNames('flex relative', className)} data-testid='submenu'>
      {({ close, open }) => (
        <div className='relative'>
          <MenuButton
            className={classNames(buttonClasses, 'focus-visible:outline-none')}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
            {buttonContent ?? <IconDotsVertical className={classNames(dotsClasses)} />}
          </MenuButton>
          <Transition
            show={open}
            as={Fragment}
            appear={true}
            enter='transition ease-out duration-100'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'>
            <MenuItems
              modal={false}
              anchor={{ to: 'bottom end', gap: 16 }}
              className='z-30 w-56 mt-2 flex flex-col gap-2 p-3 bg-primary rounded-lg shadow-[2px_2px_20px_0_rgba(0,0,0,0.25)] overflow-auto focus:outline-hidden'>
              <p className='text-left text-body-xs mb-2 text-secondary'>{t('common:optionsLabel')}</p>
              {options.map((option) => (
                <MenuItem key={option.name}>
                  {({ focus }) =>
                    option.link ? (
                      <SubmenuIconLink
                        key={`action-menu-${option.name}`}
                        {...option}
                        link={option.link}
                        isDesktop={true}
                        active={focus}
                        close={close}
                      />
                    ) : (
                      <SubmenuIconButton
                        key={`action-menu-${option.name}`}
                        {...option}
                        active={focus}
                        isDesktop={true}
                        close={close}
                      />
                    )
                  }
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </div>
      )}
    </Menu>
  );
};
